import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Play, Square, Workflow } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/native-select';
import { ernaehrungAPI } from '@/lib/api';

/* Automatik-Sequenzen am Mitglied (E7, 15.10.2026): laufende Sequenzen mit nächstem Schritt, Blockade mit Grund,
   „Jetzt starten“ (jede Sequenz) und „Stoppen“. Die Sequenzen selbst pflegt Ernährung › Automatik
   (`pages/ErnaehrungAutomatik.js`, das die Helfer von hier nimmt — so kreisen die Importe nicht). */

const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
export const SCHRITT_STATUS = {
  geplant: ['geplant', 'bg-slate-100 text-slate-700'], blockiert: ['blockiert', 'bg-amber-100 text-amber-800'],
  in_arbeit: ['unklar (unterbrochen)', 'bg-orange-100 text-orange-800'], freigabe: ['wartet auf Freigabe', 'bg-blue-100 text-blue-800'],
  gesendet: ['gesendet', 'bg-emerald-100 text-emerald-800'], uebersprungen: ['übersprungen', 'bg-slate-100 text-slate-500'],
  fehler: ['Fehler', 'bg-red-100 text-red-800'],
};
export const LAUF_STATUS = { laeuft: 'läuft', fertig: 'fertig', gestoppt: 'gestoppt' };
export const zeit = (iso) => { try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

export function StatusChip({ status }) {
  const [label, farbe] = SCHRITT_STATUS[status] || [status, 'bg-slate-100 text-slate-700'];
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${farbe}`}>{label}</span>;
}

export function ErnaehrungAutomatik({ leadId }) {
  const [d, setD] = useState(null);
  const [wahl, setWahl] = useState('');
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => ernaehrungAPI.automatikMitglied(leadId).then((r) => setD(r.data)).catch(() => setD({ laeufe: [], blockiert: [], sequenzen: [] })), [leadId]);
  useEffect(() => { laden(); }, [laden]);
  if (!d) return null;
  const tu = async (aufruf, ok) => {
    setBusy(true);
    try { await aufruf(); if (ok) toast.success(ok); await laden(); } catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  const laufend = d.laeufe.filter((l) => l.status === 'laeuft');
  const vorbei = d.laeufe.filter((l) => l.status !== 'laeuft').slice(0, 5);
  const startbar = d.sequenzen.filter((s) => !s.laeuft);
  return (
    <Card data-testid="ernaehrung-automatik-karte">
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Workflow className="h-4 w-4 text-primary" aria-hidden="true" />Automatik</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        {d.blockiert.length > 0 && <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">Blockiert: {d.blockiert.join(', ')} — Schritte warten, bis das frei ist.</p>}
        {laufend.length === 0 ? <p className="text-xs text-muted-foreground">Keine Sequenz läuft.</p> : (
          <ul className="space-y-2">
            {laufend.map((l) => (
              <li key={l.id} className="rounded-md border p-2">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{l.sequenz_name}</p>
                    <p className="text-xs text-muted-foreground">{l.schritte_erledigt} von {l.schritte_gesamt} Schritten · seit {zeit(l.ausloeser_am)}</p>
                    {l.naechster && <p className="mt-1 text-xs">Nächster: Schritt {l.naechster.nr} am {zeit(l.naechster.faellig_am)} <StatusChip status={l.naechster.status} />{l.naechster.grund ? <span className="text-muted-foreground"> — {l.naechster.grund}</span> : null}</p>}
                  </div>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => { if (window.confirm(`„${l.sequenz_name}“ stoppen? Offene Schritte werden übersprungen.`)) tu(() => ernaehrungAPI.automatikStoppen(l.id, 'am Mitglied gestoppt'), 'Gestoppt'); }}><Square className="mr-1 h-4 w-4" />Stoppen</Button>
                </div>
                <ol className="mt-1 space-y-0.5 text-xs">
                  {l.schritte.map((s) => <li key={s.nr} className="flex items-center gap-1.5"><span className="w-14 shrink-0 text-muted-foreground">Schritt {s.nr}</span><StatusChip status={s.status} /><span className="truncate text-muted-foreground">{s.status === 'gesendet' ? zeit(s.gesendet_am) : zeit(s.faellig_am)}{s.grund ? ` · ${s.grund}` : ''}</span></li>)}
                </ol>
              </li>
            ))}
          </ul>
        )}
        {vorbei.length > 0 && <p className="text-xs text-muted-foreground">Zuletzt: {vorbei.map((l) => `${l.sequenz_name} (${LAUF_STATUS[l.status] || l.status})`).join(', ')}</p>}
        {d.sequenzen.length === 0 ? <p className="text-xs text-muted-foreground">Noch keine Sequenz angelegt — <Link to="/ernaehrung?reiter=automatik" className="underline">Ernährung › Automatik</Link>.</p> : (
          <div className="flex items-center gap-2">
            <NativeSelect aria-label="Sequenz starten" className="flex-1" value={wahl} onChange={setWahl} options={[{ value: '', label: startbar.length ? 'Sequenz wählen …' : 'alle Sequenzen laufen schon' }, ...startbar.map((s) => ({ value: s.id, label: `${s.name} (${s.schritte} Schritte)` }))]} />
            <Button size="sm" disabled={busy || !wahl} onClick={() => tu(() => ernaehrungAPI.automatikStarten(wahl, leadId).then(() => setWahl('')), 'Gestartet')}>{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}Jetzt starten</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
